import { useCallback, useEffect, useState } from "react";

export type CalendarView = "day" | "3-day" | "week" | "agenda";
export type CalendarZoom = 1 | 2 | 4;
export type WeekStart = 0 | 1 | 6; // Sun | Mon | Sat (Date.getDay() values)
export type HourFormat = "12" | "24";

export interface CalendarPrefs {
  view: CalendarView;
  zoom: CalendarZoom;
  weekStart: WeekStart;
  hourFormat: HourFormat;
  /** ISO 639-1 + region, e.g. "en-US". `null` = follow browser. */
  locale: string | null;
}

const DEFAULT_PREFS: CalendarPrefs = {
  view: "day",
  zoom: 1,
  weekStart: 0,
  hourFormat: "12",
  locale: null,
};

function isMobile(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;
}

function storageKey(tenant: string): string {
  return `zedule:cal:${tenant}`;
}

function read(tenant: string): CalendarPrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(storageKey(tenant));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CalendarPrefs>;
      return { ...DEFAULT_PREFS, ...parsed };
    }
  } catch {
    // Corrupt localStorage value — fall through to default.
  }
  // First-time defaults: agenda on mobile, day on desktop. Doesn't persist
  // until the user touches a control, so we don't pollute the key needlessly.
  return { ...DEFAULT_PREFS, view: isMobile() ? "agenda" : "day" };
}

function write(tenant: string, prefs: CalendarPrefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(tenant), JSON.stringify(prefs));
  } catch {
    // Quota exceeded or storage disabled — silently skip.
  }
}

/**
 * Per-tenant calendar preferences hook. Returns the current prefs and a
 * setter that merges partials and persists to localStorage.
 */
export function useCalendarPrefs(tenantSlug: string | undefined) {
  const tenant = tenantSlug ?? "_default";
  const [prefs, setPrefs] = useState<CalendarPrefs>(() => read(tenant));

  // Re-read when tenant changes (e.g., user switches business)
  useEffect(() => {
    setPrefs(read(tenant));
  }, [tenant]);

  const update = useCallback(
    (patch: Partial<CalendarPrefs>) => {
      setPrefs((curr) => {
        const next = { ...curr, ...patch };
        write(tenant, next);
        return next;
      });
    },
    [tenant],
  );

  return [prefs, update] as const;
}
